import {createNewsletterRecovery} from './newsletter_recovery.js?v=molten-newsletter-recovery-20261011-r3';
import {recoverProductParams,normaliseNativeFields,createNativePost} from './native_form_adapter.js?v=molten-accepted-analytics-20261019-r1';
/** Local transport preparation only; no default network, DOM or storage effects. */
const families = new Set(['bespoke-design','general','gemstone-diamond','gemstone-product','remodelling','gold-swap']);
const allowed = new Set(['first_name','last_name','name','email','message','phone','preferred_initial_contact_method','product_handle','client_reported_source_page']);
function snapshot(fields) {
  if (!fields || typeof fields !== 'object' || Array.isArray(fields) || Object.keys(fields).some(k=>!allowed.has(k))) throw new Error('Invalid field envelope');
  return Object.fromEntries(Object.keys(fields).sort().map(k=>{
    if(typeof fields[k] !== 'string') throw new Error('String fields required');
    return [k,fields[k]];
  }));
}
function createSubmission({family,post,subscribeNewsletter=async()=>{},journal=null,onAcceptedReceipt=async()=>{}}) {
  if (!families.has(family) || typeof post!=='function' || typeof subscribeNewsletter!=='function' || typeof onAcceptedReceipt!=='function') throw new Error('Explicit approved route and adapters required');
  let ticket=null, savedFields=null, identity=null, busy=false, newsletterAttempted=false;
  let restoredConsent=false;
  if(journal) {
    if(typeof journal.read!=='function'||typeof journal.write!=='function'||typeof journal.clear!=='function')throw Error('Explicit recovery journal required');
    const previous=journal.read();
    if(previous?.version===2) {
      if(previous.family!==family||typeof previous.submissionId!=='string'||!/^[a-f0-9]{64}$/.test(previous.submissionId)||!Number.isSafeInteger(previous.time)||Date.now()-previous.time<0)throw Error('Invalid accepted recovery identity');
      if(Date.now()-previous.time>=20*60*1000) {journal.clear();}
      else {identity=previous.submissionId;journal.clear();}
    } else if(previous) {
      if(previous.version!==1||previous.family!==family||typeof previous.ticket!=='string'||!/^[A-Za-z0-9_-]{32,200}$/.test(previous.ticket))throw Error('Recovery identity unavailable');
      ticket=previous.ticket;savedFields=snapshot(previous.fields);
      restoredConsent=previous.newsletterChecked===true;
    }
  }
  return {
    acceptedIdentity() {return identity;},
    pendingFields() {return savedFields?{...savedFields}:null;},
    pendingNewsletterConsent() {return restoredConsent;},
    updateNewsletterConsent(checked) {
      if(typeof checked!=='boolean')throw Error('Explicit checkbox state required');
      restoredConsent=checked;
      if(journal&&ticket&&savedFields)journal.write({version:1,family,ticket,fields:{...savedFields},newsletterChecked:checked});
    },
    async submit({fields,challengeToken,newsletterChecked=false}) {
      const current=snapshot(fields);
      if(savedFields && JSON.stringify(current)!==JSON.stringify(savedFields)) throw new Error('Resolve existing submission before changing its payload');
      if(identity) return {state:'accepted',submissionId:identity};
      if(busy) return {state:'in-progress'};
      if(typeof challengeToken!=='string' || !challengeToken) return {state:'verification-required'};
      busy=true;
      try {
        if(!ticket) {
          const issued=await post(`/apps/molten-enquiries/v1/${family}/ticket`,{});
          if(typeof issued?.ticket!=='string'||!/^[A-Za-z0-9_-]{32,200}$/.test(issued.ticket)) return {state:'unavailable'};
          ticket=issued.ticket;
        }
        // Retain the exact ticket and snapshot on every ambiguous outcome.
        savedFields??=current;
        // A recoverable browser journal must commit before the request. Losing
        // an uncertain identity on reload must never silently issue another.
        if(journal)journal.write({version:1,family,ticket,fields:{...savedFields},newsletterChecked:newsletterChecked===true});
        let receipt;
        try { receipt=await post(`/apps/molten-enquiries/v1/${family}/submit`,{ticket,fields:{...savedFields},challenge_token:challengeToken}); }
        catch { return {state:'uncertain'}; }
        // Only explicit receiver proof of nonacceptance can release the frozen
        // fields or replace an expired ticket. Generic errors/timeouts cannot.
        if(receipt?.accepted===false && receipt.code==='ticket_expired') {
          if(journal)journal.clear();
          ticket=null;savedFields=null;
          return {state:'verification-required'};
        }
        if(receipt?.accepted===false && receipt.code==='fields_rejected') {
          const retainedFields={...savedFields};
          if(journal)journal.clear();
          ticket=null;savedFields=null;
          return {state:'fields-rejected',retainedFields};
        }
        if(receipt?.accepted===false && receipt.code==='verification_failed') {
          if(journal)journal.clear();
          savedFields=null;
          return {state:'verification-required'};
        }
        if(receipt?.accepted!==true || typeof receipt.submission_id!=='string' || !/^[a-f0-9]{64}$/.test(receipt.submission_id)) return {state:'uncertain'};
        const acceptedEmail=savedFields.email;
        identity=receipt.submission_id;
        try { await onAcceptedReceipt({submissionId:identity,email:acceptedEmail,newsletterChecked:newsletterChecked===true}); }
        catch {
          // The receipt is already verified; cache it and never resend in this controller.
          if(journal)try{journal.write({version:2,family,submissionId:identity,time:Date.now()});}catch{}
          savedFields=null;
          return {state:'accepted',submissionId:identity,newsletter:newsletterChecked?'retry-separately':'not-requested',newsletterEmail:newsletterChecked?acceptedEmail:undefined};
        }
        if(journal) {try {journal.clear();} catch { /* A retained accepted identity is safe to replay. */ }}
        savedFields=null;
        let newsletter='not-requested';
        if(newsletterChecked===true && !newsletterAttempted) {
          newsletterAttempted=true;
          try { await subscribeNewsletter({email:acceptedEmail,family,submissionId:identity});newsletter='requested'; }
          catch { newsletter='retry-separately'; }
        }
        return {state:'accepted',submissionId:identity,newsletter};
      } catch { return {state:'unavailable'}; }
      finally {busy=false;}
    }
  };
}

import {loadVendor,createWidgetVerifier} from './turnstile_browser.js?v=molten-verification-diagnostics-20261020-r1';

function normaliseGeneralFields(values,family){
 const base=normaliseNativeFields(values,family);
 const first=values['contact[first_name]'],last=values['contact[last_name]'];
 if(typeof first!=='string'||typeof last!=='string'||!first.trim()||!last.trim())throw Error('Name parts required');
 const first_name=first.trim(),last_name=last.trim(),name=first_name+' '+last_name;
 if(first_name.length>100||last_name.length>100||name.length>200)throw Error('Name length exceeded');
 return {...base,name,first_name,last_name};
}

/** Draft connection preparation. Called explicitly by the existing form source.
 * No module-level DOM, submission, newsletter or vendor action.
 */
const notices=Object.freeze({
 verification:'We could not verify your submission. Your details are still here, so you can try again.',
 unavailable:'We could not complete the verification needed to send your enquiry. Your details are still here, so you can try again.',
 uncertain:'We could not confirm whether your enquiry was submitted. Your details are still here. Please try again.'
});
function createSessionJournal(storage,key,path) {
 return {
  read(){const raw=storage.getItem(key);if(!raw)return null;const value=JSON.parse(raw);if(value.path!==path)throw Error('Pending enquiry requires its original page');return value;},
  write(value){const raw=JSON.stringify({...value,path});storage.setItem(key,raw);if(storage.getItem(key)!==raw)throw Error('Recovery storage unavailable');},
  clear(){storage.removeItem(key);}
 };
}
function createDirectForm({page,family,verify,fetchImpl,storage,path,onAccepted,subscribeNewsletter,onAcceptedReceipt}) {
 const form=page.querySelector('#MoltenEnquiry');
 const button=form.querySelector('[data-enquiry-primary-action]');
 const notice=page.querySelector('[data-enquiry-recovery-notice]');
 if(!button||!notice||typeof verify!=='function'||typeof onAccepted!=='function')throw Error('Approved form scaffold and adapters required');
 for(const [name,limit]of Object.entries({'contact[name]':200,'contact[email]':254,'contact[phone]':80,'contact[body]':10000})){const input=[...form.elements].find(el=>el.name===name);if(input)input.maxLength=limit;}
 let running=false,accepted=false,held=null,controller=null;
 const show=kind=>{form.dataset.enquiryState='error';notice.textContent=notices[kind];notice.hidden=false;button.textContent='Try again';button.disabled=false;};
 try {controller=createSubmission({family,post:createNativePost(fetchImpl),journal:createSessionJournal(storage,`molten-direct-enquiry-${family}-v1`,path),subscribeNewsletter,onAcceptedReceipt});held=controller.pendingFields(); if(controller.acceptedIdentity()){accepted=true;onAccepted({state:'accepted',submissionId:controller.acceptedIdentity()});}}
 catch {show('unavailable');}
 const fieldNames={name:'contact[name]',first_name:'contact[first_name]',last_name:'contact[last_name]',email:'contact[email]',message:'contact[body]',phone:'contact[phone]',preferred_initial_contact_method:'contact[Preferred initial contact method]',product_handle:family==='gemstone-product'?'contact[Gemstone handle]':'contact[Design handle]',client_reported_source_page:'contact[Source page]'};
 const restore=()=>{if(!held)return;for(const [key,value]of Object.entries(held)){for(const el of [...form.elements].filter(x=>x.name===fieldNames[key])){if(el.type==='radio')el.checked=el.value===value;else el.value=value;}}form.querySelector('input[type=radio]:checked')?.dispatchEvent(new Event('change'));};
 const controls=[...form.elements].filter(el=>['INPUT','TEXTAREA','SELECT'].includes(el.tagName)&&el.type!=='hidden'&&el.type!=='checkbox');
 const defaults=controls.map(el=>({el,disabled:el.disabled,readOnly:el.readOnly}));
 const lock=value=>{for(const original of defaults){const el=original.el;if(['radio','checkbox'].includes(el.type)||el.tagName==='SELECT')el.disabled=value||original.disabled;else el.readOnly=value||original.readOnly;}};
 if(held&&!Object.hasOwn(held,'first_name')&&!Object.hasOwn(held,'last_name')){
  const first=form.elements['contact[first_name]'],last=form.elements['contact[last_name]'];
  first.required=false;last.required=false;first.disabled=true;last.disabled=true;
  page.querySelector('.enquiry-names').hidden=true;page.querySelector('[data-legacy-name]').hidden=false;
  form.elements['contact[name]'].type='text';form.elements['contact[name]'].readOnly=true;
 }
 restore();if(held){page.querySelector('#email-updates').checked=controller.pendingNewsletterConsent();lock(true);show('uncertain');}
 page.querySelector('#email-updates').addEventListener('change',()=>{
  if(!controller)return;
  try{controller.updateNewsletterConsent(page.querySelector('#email-updates').checked===true);}catch{show('unavailable');}
 });
 return {pendingProductHandle(){return held?(held.product_handle||''):null;},async submit(event){
  event.preventDefault(); // Always replaces native POST; never a fallback/double send.
  if(running||accepted)return;
  if(!controller){show('unavailable');return;}
  if(!form.checkValidity()){form.reportValidity();return;}
  running=true;notice.hidden=true;button.disabled=true;form.dataset.enquiryState='pending';
  try {
   let token;try {token=await verify();}catch(error) {show(error?.code==='verification_failed'?'verification':'unavailable');return;}
   if(typeof token!=='string'||!token){show('verification');return;}
   const values=Object.fromEntries(new FormData(form));
   const fields=held||normaliseGeneralFields(values,family);
   const consent=page.querySelector('#email-updates').checked===true;lock(true);
   const result=await controller.submit({fields,challengeToken:token,newsletterChecked:consent});
   held=controller.pendingFields();restore();lock(Boolean(held));
   if(result.state==='fields-rejected'){
    const first=form.elements['contact[first_name]'],last=form.elements['contact[last_name]'],full=form.elements['contact[name]'];
    page.querySelector('.enquiry-names').hidden=false;page.querySelector('[data-legacy-name]').hidden=true;
    first.disabled=false;last.disabled=false;first.readOnly=false;last.readOnly=false;first.required=true;last.required=true;
    full.type='hidden';full.readOnly=false;full.value=(first.value.trim()+' '+last.value.trim()).trim();
    show('unavailable');first.focus();return;
   }
   if(result.state==='accepted'){accepted=true;notice.hidden=true;onAccepted(result);return;}
   show(result.state==='uncertain'?'uncertain':result.state==='verification-required'?'verification':'unavailable');
  }catch {show('unavailable');}
  finally {running=false;if(!accepted)button.disabled=false;}
 }};
}

(() => {
  'use strict';
  const page = document.querySelector('.enquiry-page');
  if (!page || page.dataset.initialised) return;
  page.dataset.initialised = 'true';
  const form = page.querySelector('#MoltenEnquiry');
  const newsletter = page.querySelector('#MoltenEnquiryNewsletter');

  const firstName=form.elements['contact[first_name]'],lastName=form.elements['contact[last_name]'],fullName=form.elements['contact[name]'];
  let legacyNameMode=false;
  const syncNames=()=>{
    if(legacyNameMode||(firstName.disabled&&lastName.disabled))return;
    fullName.value=(firstName.value.trim()+' '+lastName.value.trim()).trim();
    firstName.maxLength=Math.min(100,Math.max(1,199-lastName.value.trim().length));
    lastName.maxLength=Math.min(100,Math.max(1,199-firstName.value.trim().length));
  };
  const showLegacyName=()=>{
    legacyNameMode=true;page.querySelector('.enquiry-names').hidden=true;
    firstName.required=false;lastName.required=false;firstName.disabled=true;lastName.disabled=true;
    page.querySelector('[data-legacy-name]').hidden=false;fullName.type='text';fullName.readOnly=true;
  };
  firstName.addEventListener('input',syncNames);lastName.addEventListener('input',syncNames);

  const params = new URLSearchParams(location.search);
  const toggle = page.querySelector('.general-toggle');
  if (toggle) toggle.addEventListener('click', () => {
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(open));
    page.querySelector('#general-contact-panel').hidden = !open;
    const media = page.querySelector('.general-media');
    if (media && !page.classList.contains('general-enquiry-accepted')) media.hidden = open;
  });
  const sourceField = document.createElement('input'); sourceField.type = 'hidden'; sourceField.name = 'contact[Source page]';
  try { const source = new URL(document.referrer); if (source.origin === location.origin) sourceField.value = source.pathname; } catch {}
  if (!sourceField.value) sourceField.value = location.pathname;
  page.querySelector('[data-design-fields]').append(sourceField);

  const general = page.classList.contains('general-enquiry');
  const key = general ? 'molten-general-enquiry-pending-v1' : 'molten-bespoke-enquiry-pending-v1';
  const expiry = 20 * 60 * 1000;
  const root = window.Shopify?.routes?.root || '/';
  const fields = ['contact[first_name]', 'contact[last_name]', 'contact[name]', 'contact[email]', 'contact[body]', 'contact[phone]', 'contact[Preferred initial contact method]'];
  let ready = false;
  const readPending = () => {
    try {
      const pending = JSON.parse(sessionStorage.getItem(key));
      if (!pending || Date.now() - pending.time > expiry || pending.path !== location.pathname) {
        sessionStorage.removeItem(key); return null;
      }
      return pending;
    } catch { return null; }
  };
  const save = value => { try { sessionStorage.setItem(key, JSON.stringify(value)); return true; } catch { return false; } };
  const remove = () => { try { sessionStorage.removeItem(key); } catch {} };
  const method = () => {
    const selected = form.querySelector('input[type=radio]:checked')?.value;
    page.querySelector('#phone').required = Boolean(selected && selected !== 'Email');
    page.querySelector('#phone-optional').hidden = Boolean(selected && selected !== 'Email');
  };
  form.querySelectorAll('input[type=radio]').forEach(input => input.addEventListener('change', method));
  const newsletterRecovery = createNewsletterRecovery({storage:sessionStorage,family:page.dataset.receiverFamily,path:location.pathname});
  const pending = readPending();
  const correlated = pending && params.get('enquiry_attempt') === pending.token;
  const contactAccepted = page.querySelector('[data-contact-accepted]').textContent.trim() === 'true';
  const newsletterAccepted = page.querySelector('[data-newsletter-accepted]').textContent.trim() === 'true';
  const contactErrors = page.querySelector('[data-contact-errors]').textContent.trim();
  const newsletterErrors = page.querySelector('[data-newsletter-errors]').textContent.trim();
  function returnPath(token, phase) {
    const query = new URLSearchParams();
    for (const field of ['view', 'preview_theme_id', 'design_handle', 'intent']) {
      if (params.getAll(field).length === 1) query.set(field, params.get(field));
    }
    query.set('enquiry_attempt', token); query.set('enquiry_phase', phase);
    // Custom native success redirects do not append Shopify's result flags.
    // Restore the appropriate flag only in the posted success return path;
    // the tab-specific pending token and consent remain required on return.
    query.set(phase === 'contact' ? 'contact_posted' : 'customer_posted', 'true');
    return `${location.pathname}?${query}`;
  }
  function setReturn(target, path) {
    let input = target.querySelector('input[name=return_to]');
    if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'return_to'; target.append(input); }
    input.value = path;
  }
  function showReceipt() {
    form.hidden=true;page.classList.add('general-enquiry-accepted');
    const receipt=page.querySelector('[data-enquiry-receipt]');
    const related=receipt.querySelector('[data-confirmation-related]');
    for(const node of [toggle,page.querySelector('#general-contact-panel')])if(node&&node.parentElement!==related)related.append(node);
    page.querySelector('.enquiry-welcome').hidden=true;
    const media=page.querySelector('.general-media');if(media)media.hidden=false;
    const firstReveal=receipt.hidden;receipt.hidden=false;
    if(firstReveal)receipt.focus();
  }

  function startNewsletter(state, automatic = false) {
    page.querySelector('[data-newsletter-panel]').hidden = false;
    newsletter.elements['contact[email]'].value = state.email;
    setReturn(newsletter, returnPath(state.token, 'newsletter'));
    newsletter.querySelector('button').disabled = false;
    if (!automatic) return;
    const protect = () => {
      if (window.Shopify?.captcha?.protect) {
        window.Shopify.captcha.protect(newsletter, () => newsletter.submit());
      }
    };
    if (document.readyState === 'complete') protect();
    else window.addEventListener('load', protect, { once: true });
  }
  const acceptedRecovery = newsletterRecovery.read();
  const fallbackReceipt = !acceptedRecovery && newsletterRecovery.readFallback();
  let newsletterRecovered = Boolean(acceptedRecovery || fallbackReceipt);
  const receiptWithContext = () => {
    if (page.dataset.receiverFamily === 'gold-swap') reveal(true);
    showReceipt();
  };
  if (acceptedRecovery) {
    newsletterRecovery.finish({
      token: params.getAll('enquiry_attempt').length === 1 ? params.get('enquiry_attempt') : null,
      phase: params.getAll('enquiry_phase').length === 1 ? params.get('enquiry_phase') : null,
      accepted: newsletterAccepted,
      errors: newsletterErrors
    });
    remove();
    try { sessionStorage.removeItem('molten-direct-enquiry-' + page.dataset.receiverFamily + '-v1'); } catch {}
    receiptWithContext();
    const remaining = newsletterRecovery.read();
    if (remaining?.status === 'pending') startNewsletter(remaining);
  } else if (fallbackReceipt) {
    remove(); receiptWithContext();
  } else if (correlated && params.get('enquiry_phase') === 'contact' && contactAccepted && !contactErrors) {
    // Native Shopify acceptance is a separate proof kind, never a receiver id.
    receiptWithContext();
    let acceptedNative;
    try {
      acceptedNative = newsletterRecovery.remember({
        proof: {kind:'native-contact',id:null}, token:pending.token,
        email:pending.email || '', consent:pending.consent === true, time:pending.time
      });
    } catch {}
    remove();
    newsletterRecovered = true;
    if (acceptedNative?.status === 'pending') startNewsletter(acceptedNative, true);
    else if (pending.consent && pending.email) {
      page.querySelector('[data-newsletter-panel]').hidden = false;
      newsletter.elements['contact[email]'].value = pending.email;
      newsletter.querySelector('button').disabled = false;
    }
  } else if (correlated && pending.fields && contactErrors) {
    for (const [name, value] of Object.entries(pending.fields)) {
      const inputs = [...form.querySelectorAll('input,textarea')].filter(el => el.name === name);
      for (const input of inputs) {
        if (input.type === 'radio') input.checked = input.value === value;
        else input.value = value;
      }
    }
    page.querySelector('#email-updates').checked = Boolean(pending.consent);
    method();
  }
  async function resolveDesign() {
    if (general) { ready = true; form.querySelector('[type=submit]').disabled = false; return; }
    const contextKeys = ['design_handle', 'design_title', 'product_url', 'intent'];
    if (!contextKeys.some(field => params.has(field))) {
      ready = true; form.querySelector('[type=submit]').disabled = false; return;
    }
    if (contextKeys.some(field => params.getAll(field).length > 1)) throw new Error('Invalid context');
    const handle = params.get('design_handle') || '';
    if (handle.length > 200 || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle) || params.get('intent') !== 'bespoke-design') throw new Error('Missing context');
    const url = new URL(`${root}products/${handle}`, location.origin);
    url.searchParams.set('view', 'molten-enquiry-context');
    const preview = params.get('preview_theme_id');
    if (/^\d+$/.test(preview || '')) url.searchParams.set('preview_theme_id', preview);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    let product;
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('Product unavailable');
      product = await response.json();
    } finally { clearTimeout(timer); }
    const title = product.displayTitle;
    if (product.handle !== handle || typeof title !== 'string' || !title.trim() || title.length > 500 || /[\u0000-\u001f\u007f]/.test(title)) throw new Error('Invalid product');
    const reference = page.querySelector('[data-design-reference]');
    reference.querySelector('.enquiry-design-title').textContent = title;
    const back = reference.querySelector('a');
    back.href = `${root}products/${handle}`; back.hidden = false;
    const backQuery = new URLSearchParams();
    if (/^\d+$/.test(preview || '')) backQuery.set('preview_theme_id', preview);
    // Only the known same-origin product review views may survive the return.
    // Caller-supplied product_url never becomes a navigation destination.
    try {
      const source = new URL(document.referrer);
      const sourceView = source.searchParams.get('view');
      if (source.origin === location.origin && source.pathname === `${root}products/${handle}` &&
          ['bespoke-pdp-r3-review', 'bespoke-pdp-saved-qa'].includes(sourceView)) {
        backQuery.set('view', sourceView);
      }
    } catch {}
    back.search = backQuery.toString();
    const image = reference.querySelector('img');
    if (typeof product.image === 'string') {
      const source = new URL(product.image, location.origin);
      if (source.protocol === 'https:' && (source.hostname === location.hostname || source.hostname === 'cdn.shopify.com' || source.hostname === 'www.moltenstore.com')) {
        image.addEventListener('load', () => { image.hidden = false; }, { once: true });
        image.addEventListener('error', () => { image.hidden = true; }, { once: true });
        image.alt = title; image.src = source.href;
      }
    }
    reference.hidden = false;
    const context = {
      'contact[Design]': title,
      'contact[Design handle]': handle,
      'contact[Design URL]': `https://www.moltenstore.com/products/${handle}`,
      'contact[Enquiry intent]': 'bespoke-design'
    };
    for (const [name, value] of Object.entries(context)) {
      const input = document.createElement('input'); input.type = 'hidden'; input.name = name; input.value = value;
      page.querySelector('[data-design-fields]').append(input);
    }
    ready = true; form.querySelector('[type=submit]').disabled = false;
  }
  const family=page.dataset.receiverFamily;
  let clientVerifier;
  const direct=createDirectForm({page,family,fetchImpl:window.fetch.bind(window),storage:window.sessionStorage,path:location.pathname,
    verify:async()=>{
      if(!clientVerifier){const vendor=await loadVendor(document,window);clientVerifier=createWidgetVerifier({vendor,container:page.querySelector('[data-enquiry-verification]'),family,siteKey:'0x4AAAAAAFSNLvKvsAMqGacF',compact:form.getBoundingClientRect().width<300});}
      return clientVerifier();
    },
    onAcceptedReceipt:({submissionId,email,newsletterChecked})=>{
      newsletterRecovery.remember({proof:{kind:'receiver',id:submissionId},token:crypto.randomUUID(),email,consent:newsletterChecked});
      remove(); newsletterRecovered=true;
    },
    onAccepted:(result)=>{
      newsletterRecovered=true; receiptWithContext();
      if(result.newsletter==='retry-separately' && result.newsletterEmail){
        page.querySelector('[data-newsletter-panel]').hidden=false;
        newsletter.elements['contact[email]'].value=result.newsletterEmail;
        newsletter.querySelector('button').disabled=false;
      }
    },
    subscribeNewsletter:async({email,family,submissionId})=>{
      const state=newsletterRecovery.read();
      if(!state||state.proof.kind!=='receiver'||state.proof.id!==submissionId)throw Error('Accepted newsletter proof unavailable');
      receiptWithContext();
      if(state.status==='pending')startNewsletter(state,true);
    }
  });
  // Recover and resolve the original held product before enabling retry.
  recoverProductParams(params,family,direct.pendingProductHandle());
  form.addEventListener('submit', event => {
    if (newsletterRecovered) { event.preventDefault(); return; }
    event.preventDefault();
    if(!ready)return;
    syncNames();
    if(!form.checkValidity()){form.reportValidity();return;}
    direct.submit(event);
  });
  resolveDesign().catch(() => { page.querySelector('[data-context-error]').hidden = false; });
})();

